/* ============================================
   OPULENT FRAGRANCE — Custom JavaScript
   Scroll animations, parallax, header state
   ============================================ */

(function() {
  'use strict';

  /* --- Sticky Transparent Header --- */
  const header = document.querySelector('.section-header');
  if (header) {
    let lastScroll = 0;
    const scrollThreshold = 50;

    function updateHeader() {
      const currentScroll = window.pageYOffset;
      if (currentScroll > scrollThreshold) {
        header.classList.add('header-scrolled');
      } else {
        header.classList.remove('header-scrolled');
      }
      lastScroll = currentScroll;
    }

    window.addEventListener('scroll', updateHeader, { passive: true });
    updateHeader();
  }

  /* --- Fade-on-Scroll Reveal --- */
  function initRevealAnimations() {
    const revealElements = document.querySelectorAll('.ss-reveal, .ss-stagger');
    if (!revealElements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('ss-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -60px 0px'
    });

    revealElements.forEach(el => observer.observe(el));
  }

  /* --- Auto-apply reveal to Dawn sections --- */
  function autoApplyReveal() {
    const sections = document.querySelectorAll(
      '.shopify-section:not(.shopify-section-group-header-group):not(.shopify-section-group-footer-group):not(.ss-no-reveal)'
    );
    sections.forEach(section => {
      // Skip sections that already use Dawn's built-in scroll-trigger animation system.
      // Those sections manage their own opacity/transform animations on inner elements,
      // and wrapping the outer section with ss-reveal (opacity:0) conflicts with them,
      // causing blog posts and other content to blank or fade incorrectly.
      if (section.querySelector('.scroll-trigger')) return;

      if (!section.classList.contains('ss-reveal') && !section.querySelector('.ss-parallax-hero') && !section.querySelector('.ss-video-hero') && !section.querySelector('.ss-marquee')) {
        section.classList.add('ss-reveal');
      }
    });
  }

  /* --- Parallax Effect --- */
  function initParallax() {
    const parallaxMedia = document.querySelectorAll('.ss-parallax-hero__media');
    if (!parallaxMedia.length) return;

    function updateParallax() {
      parallaxMedia.forEach(media => {
        const hero = media.parentElement;
        const rect = hero.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        if (rect.bottom > 0 && rect.top < windowHeight) {
          const scrollPercent = (windowHeight - rect.top) / (windowHeight + rect.height);
          const translateY = (scrollPercent - 0.5) * 80;
          media.style.transform = `translateY(${translateY}px)`;
        }
      });
    }

    window.addEventListener('scroll', updateParallax, { passive: true });
    updateParallax();
  }

  /* --- Marquee Duplication --- */
  function initMarquee() {
    const marquees = document.querySelectorAll('.ss-marquee');
    marquees.forEach(marquee => {
      const track = marquee.querySelector('.ss-marquee__track');
      if (!track) return;
      
      const content = track.innerHTML;
      track.innerHTML = content + content;
    });
  }

  /* --- Recently Viewed Products --- */
  function initRecentlyViewed() {
    const STORAGE_KEY = 'ss-recently-viewed';
    const MAX_ITEMS = 12;

    // Track current product
    function trackProduct() {
      const productPage = document.querySelector('[data-product-id]');
      if (!productPage) return;

      const handle = window.location.pathname.split('/products/')[1];
      if (!handle) return;

      let viewed = [];
      try {
        viewed = JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || [];
      } catch (e) {
        viewed = [];
      }

      // Remove if already exists, then add to front
      viewed = viewed.filter(h => h !== handle);
      viewed.unshift(handle);
      viewed = viewed.slice(0, MAX_ITEMS);

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(viewed));
      } catch (e) {}
    }

    // Render recently viewed section
    function renderRecentlyViewed() {
      const sections = document.querySelectorAll('.ss-recently-viewed');
      if (!sections.length) return;

      let viewed = [];
      try {
        viewed = JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || [];
      } catch (e) {
        return;
      }

      sections.forEach(section => {
        const currentProduct = section.getAttribute('data-current-product');
        const maxProducts = parseInt(section.getAttribute('data-products-to-show')) || 4;
        const grid = section.querySelector('.ss-recently-viewed__grid');
        if (!grid) return;

        // Filter out current product
        const handles = viewed.filter(h => h !== currentProduct).slice(0, maxProducts);
        if (!handles.length) return;

        // Fetch product data and render
        const fetches = handles.map(handle =>
          fetch(`/products/${handle}.json`)
            .then(r => r.ok ? r.json() : null)
            .catch(() => null)
        );

        Promise.all(fetches).then(products => {
          const validProducts = products.filter(p => p && p.product);
          if (!validProducts.length) return;

          grid.innerHTML = validProducts.map(data => {
            const p = data.product;
            const img = p.images && p.images[0] ? p.images[0].src : '';
            const price = p.variants && p.variants[0] ? p.variants[0].price : '0.00';
            const comparePrice = p.variants && p.variants[0] ? p.variants[0].compare_at_price : null;
            const url = `/products/${p.handle}`;

            let priceHTML = '';
            if (comparePrice && parseFloat(comparePrice) > parseFloat(price)) {
              priceHTML = `<span class="ss-compare-price">$${parseFloat(comparePrice).toFixed(2)}</span> <span class="ss-sale-price">$${parseFloat(price).toFixed(2)}</span>`;
            } else {
              priceHTML = `$${parseFloat(price).toFixed(2)}`;
            }

            return `
              <div class="grid__item ss-recently-viewed__card">
                <a href="${url}" class="ss-recently-viewed__card-image">
                  ${img ? `<img src="${img}&width=400" alt="${p.title}" loading="lazy" width="400" height="400">` : ''}
                </a>
                <h3 class="ss-recently-viewed__card-title"><a href="${url}">${p.title}</a></h3>
                <div class="ss-recently-viewed__card-price">${priceHTML}</div>
              </div>
            `;
          }).join('');

          section.style.display = '';
        });
      });
    }

    trackProduct();
    renderRecentlyViewed();
  }

  /* --- Remove Shopify Buyer Consent Block ---
     shopify-accelerated-checkout renders a subscription consent element
     (#shopify-buyer-consent) even when no selling plans exist on the store.
     This observer removes it from the DOM entirely whenever it appears. */
  function initRemoveBuyerConsent() {
    function removeBuyerConsent() {
      var el = document.getElementById('shopify-buyer-consent');
      if (el) el.remove();
    }

    // Remove immediately if already present
    removeBuyerConsent();

    // Watch for it being injected asynchronously by the web component
    var observer = new MutationObserver(function() {
      removeBuyerConsent();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* --- Initialize Everything --- */
  function init() {
    autoApplyReveal();
    initRevealAnimations();
    initParallax();
    initMarquee();
    initRecentlyViewed();
    initRemoveBuyerConsent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();